import React, { useState } from 'react';
import { BarChart3, PieChart, TrendingUp, BarChart, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ChartsGraphs = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    charts: initialData.charts || [],
    chartSettings: initialData.chartSettings || {
      theme: 'light',
      colorPalette: 'default',
      showLegend: true,
      showGrid: true,
      responsive: true
    },
    autoGenerate: initialData.autoGenerate || true
  });

  const chartTypes = [
    { value: 'bar', label: t('workflow.draftReport.chartsGraphs.chartTypes.bar.label'), icon: BarChart, description: t('workflow.draftReport.chartsGraphs.chartTypes.bar.description') },
    { value: 'line', label: t('workflow.draftReport.chartsGraphs.chartTypes.line.label'), icon: TrendingUp, description: t('workflow.draftReport.chartsGraphs.chartTypes.line.description') },
    { value: 'pie', label: t('workflow.draftReport.chartsGraphs.chartTypes.pie.label'), icon: PieChart, description: t('workflow.draftReport.chartsGraphs.chartTypes.pie.description') },
    { value: 'area', label: t('workflow.draftReport.chartsGraphs.chartTypes.area.label'), icon: BarChart3, description: t('workflow.draftReport.chartsGraphs.chartTypes.area.description') }
  ];

  const colorPalettes = [
    { value: 'default', label: t('workflow.draftReport.chartsGraphs.colorPalettes.default'), colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'] },
    { value: 'green', label: t('workflow.draftReport.chartsGraphs.colorPalettes.green'), colors: ['#10B981', '#059669', '#047857', '#065F46', '#064E3B'] },
    { value: 'blue', label: t('workflow.draftReport.chartsGraphs.colorPalettes.blue'), colors: ['#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF', '#1E3A8A'] },
    { value: 'purple', label: t('workflow.draftReport.chartsGraphs.colorPalettes.purple'), colors: ['#8B5CF6', '#7C3AED', '#6D28D9', '#5B21B6', '#4C1D95'] }
  ];

  const sampleData = {
    emissions: {
      title: t('workflow.draftReport.chartsGraphs.sampleData.emissions.title'),
      type: 'bar',
      data: [
        { name: 'Scope 1', value: 1250, unit: 'tCO2e' },
        { name: 'Scope 2', value: 3200, unit: 'tCO2e' },
        { name: 'Scope 3', value: 8500, unit: 'tCO2e' }
      ]
    },
    trends: {
      title: t('workflow.draftReport.chartsGraphs.sampleData.trends.title'),
      type: 'line',
      data: [
        { year: '2020', value: 12000 },
        { year: '2021', value: 11500 },
        { year: '2022', value: 11000 },
        { year: '2023', value: 12950 }
      ]
    },
    sources: {
      title: t('workflow.draftReport.chartsGraphs.sampleData.sources.title'),
      type: 'pie',
      data: [
        { name: 'Electricity', value: 35, percentage: 35 },
        { name: 'Transportation', value: 25, percentage: 25 },
        { name: 'Manufacturing', value: 20, percentage: 20 },
        { name: 'Waste', value: 15, percentage: 15 },
        { name: 'Other', value: 5, percentage: 5 }
      ]
    }
  };

  const addChart = (chartType) => {
    const newChart = {
      id: Date.now(),
      title: `New ${chartTypes.find(t => t.value === chartType)?.label}`,
      type: chartType,
      data: [],
      settings: {
        showLegend: true,
        showGrid: true,
        color: colorPalettes[0].colors[0]
      }
    };

    setFormData(prev => ({
      ...prev,
      charts: [...prev.charts, newChart]
    }));
  };

  const removeChart = (chartId) => {
    setFormData(prev => ({
      ...prev,
      charts: prev.charts.filter(chart => chart.id !== chartId)
    }));
  };



  const generateSampleCharts = () => {
    const charts = Object.entries(sampleData).map(([key, data], index) => ({
      id: Date.now() + index,
      title: data.title,
      type: data.type,
      data: data.data,
      settings: {
        showLegend: true,
        showGrid: true,
        color: colorPalettes[0].colors[index % colorPalettes[0].colors.length]
      }
    }));

    setFormData(prev => ({
      ...prev,
      charts: charts
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderChartPreview = (chart) => {
    const IconComponent = chartTypes.find(t => t.value === chart.type)?.icon || BarChart3;
    
    return (
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <IconComponent className="w-5 h-5 text-blue-500 mr-2" />
            <span className="font-medium text-gray-800">{chart.title}</span>
          </div>
          <button
            type="button"
            onClick={() => removeChart(chart.id)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        
        <div className="h-32 bg-white rounded border flex items-center justify-center">
          <div className="text-center text-gray-500">
            <IconComponent className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">{chart.type.toUpperCase()} Chart</p>
            <p className="text-xs">{chart.data.length} data points</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center mb-6">
          <BarChart3 className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{t('workflow.draftReport.chartsGraphs.title')}</h2>
            <p className="text-gray-600">{t('workflow.draftReport.chartsGraphs.description')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Auto Generation Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-800">{t('workflow.draftReport.chartsGraphs.autoGenerate')}</h3>
              <p className="text-sm text-gray-600">{t('workflow.draftReport.chartsGraphs.autoGenerateDescription')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.autoGenerate}
                onChange={(e) => handleInputChange('autoGenerate', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Generate Sample Charts Button */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={generateSampleCharts}
              className="px-6 py-3 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors duration-200"
            >
              {t('workflow.draftReport.chartsGraphs.generateSampleCharts')}
            </button>
          </div>

          {/* Chart Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.chartsGraphs.theme')}
              </label>
              <select
                value={formData.chartSettings.theme}
                onChange={(e) => handleInputChange('chartSettings', {
                  ...formData.chartSettings,
                  theme: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="light">Light Theme</option>
                <option value="dark">Dark Theme</option>
                <option value="auto">Auto (System)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.chartsGraphs.colorPalette')}
              </label>
              <select
                value={formData.chartSettings.colorPalette}
                onChange={(e) => handleInputChange('chartSettings', {
                  ...formData.chartSettings,
                  colorPalette: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {colorPalettes.map(palette => (
                  <option key={palette.value} value={palette.value}>
                    {palette.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Chart Options */}
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.chartSettings.showLegend}
                onChange={(e) => handleInputChange('chartSettings', {
                  ...formData.chartSettings,
                  showLegend: e.target.checked
                })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{t('workflow.draftReport.chartsGraphs.showLegend')}</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.chartSettings.showGrid}
                onChange={(e) => handleInputChange('chartSettings', {
                  ...formData.chartSettings,
                  showGrid: e.target.checked
                })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{t('workflow.draftReport.chartsGraphs.showGrid')}</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.chartSettings.responsive}
                onChange={(e) => handleInputChange('chartSettings', {
                  ...formData.chartSettings,
                  responsive: e.target.checked
                })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{t('workflow.draftReport.chartsGraphs.responsive')}</span>
            </label>
          </div>

          {/* Add Chart Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.draftReport.chartsGraphs.addChart')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {chartTypes.map(chartType => {
                const IconComponent = chartType.icon;
                return (
                  <button
                    key={chartType.value}
                    type="button"
                    onClick={() => addChart(chartType.value)}
                    className="p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-center"
                  >
                    <IconComponent className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <h4 className="font-medium text-gray-800">{chartType.label}</h4>
                    <p className="text-xs text-gray-600 mt-1">{chartType.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Charts List */}
          {formData.charts.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Generated Charts ({formData.charts.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {formData.charts.map(chart => renderChartPreview(chart))}
              </div>
            </div>
          )}


        </form>
      </div>
    </div>
  );
};

export default ChartsGraphs;
